import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/buttons/button';
import { Input } from '../../../components/ui/input';
import { entityService } from '../../../services/entityService';
import { useGeoLocation } from '../../../hooks/useGeoLocation';
import { toastService } from '../../../services/toastService';
import CustomDropdown from '../../../components/ui/CustomDropdown';
import { Label } from '../../../components/ui/label';
import SingleDatePicker from '../../../components/common/SingleDatePicker';
import { useSync } from '../../../hooks/useSync';
import { db } from '../../../lib/database';
import { syncService } from '../../../services/syncService';
import { API_ENDPOINTS } from '../../../config/api';
import { ENTITY_LABELS } from '../../../constants/registryLabels';

// ─── Icons ────────────────────────────────────────────────────────────────────

const LocationIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const RefreshIcon = ({ spinning = false }) => (
  <svg
    className={`w-4 h-4 ${spinning ? 'animate-spin' : ''}`}
    fill="none" viewBox="0 0 24 24" stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────

const EntityCreationMiniForm = ({ form, onEntityCreated, initialData = null, readOnly = false, mode = null }) => {
  const navigate = useNavigate();
  // Derive effective mode: if mode not supplied, infer from context
  const effectiveMode = mode || (initialData ? 'log' : 'create');
  const [submitAction, setSubmitAction] = useState('save_and_continue');
  const [recordType, setRecordType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState(initialData?.attributes_json || {});
  const [status, setStatus] = useState(initialData?.status || '');
  const { locationData: liveLocation, isLocationLoading, refreshLocation, permissionDenied, positionUnavailable, locationFetched } = useGeoLocation();
  const { isOnline } = useSync();

  // In read-only mode use the stored coordinates, otherwise use live GPS
  const locationData = readOnly
    ? { latitude: initialData?.location_lat, longitude: initialData?.location_lng }
    : liveLocation;

  const hasLocation = !!locationData?.latitude;

  // ── Fetch record type schema ─────────────────────────────────────────────
  useEffect(() => {
    const fetchRecordType = async () => {
      const typeId = form.linked_entity_type_id || initialData?.entity_type_id;
      if (!typeId) return;
      setLoading(true);
      try {
        const response = await entityService.getEntityType(typeId);
        const data = response?.data || response;
        setRecordType(data);
        if (!initialData && !readOnly && data?.statuses_json?.length > 0) {
          setStatus(data.statuses_json[0].value);
        }
      } catch {
        toastService.error('Failed to load record type details');
      } finally {
        setLoading(false);
      }
    };
    fetchRecordType();
  }, [form.linked_entity_type_id, initialData]);

  // ── Submit / confirm ─────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    // ── Log / create-confirm: just pass through the initial entity ──────────
    if (effectiveMode === 'log' || (effectiveMode === 'create' && initialData && readOnly)) {
      onEntityCreated(initialData);
      return;
    }

    // ── Update: save changed entity attributes then continue ────────────────
    if (effectiveMode === 'update' && initialData) {
      if (!hasLocation) {
        toastService.warning('GPS coordinates are required');
        refreshLocation();
        return;
      }
      setCreating(true);
      try {
        const payload = {
          entity_type_id: initialData.entity_type_id || form.linked_entity_type_id,
          primary_value: formData[recordType?.primary_field] || initialData.primary_value || '',
          attributes_json: formData,
          status,
          location_lat: locationData.latitude,
          location_lng: locationData.longitude,
        };
        const response = await entityService.update(initialData.id, payload);
        toastService.success(ENTITY_LABELS.RECORD_UPDATED);
        onEntityCreated(response?.data || response);
      } catch {
        toastService.error('Failed to update record');
      } finally {
        setCreating(false);
      }
      return;
    }

    // ── Create: new entity ─────────────────────────────────────────────────
    if (!hasLocation) {
      toastService.warning('GPS coordinates are required');
      refreshLocation();
      return;
    }

    setCreating(true);
    const payload = {
      entity_type_id: form.linked_entity_type_id,
      primary_value: formData[recordType?.primary_field] || '',
      attributes_json: formData,
      status,
      location_lat: locationData.latitude,
      location_lng: locationData.longitude,
    };

    try {
      let createdEntity;
      if (isOnline) {
        const response = await entityService.create({ ...payload, submission_mode: 'online' });
        toastService.success(ENTITY_LABELS.RECORD_CREATED);
        createdEntity = response?.data || response;
      } else {
        const tempId = `temp_ent_${Date.now()}`;
        const offlinePayload = { ...payload, submission_mode: 'offline', tempId };
        const recordForDb = { ...payload, id: tempId, syncStatus: 'pending', created_at: new Date().toISOString() };
        await db.entities.add(recordForDb);
        await syncService.addToOutbox({
          type: 'entity',
          payload: offlinePayload,
          endpoint: API_ENDPOINTS.entities.create,
          tempId
        });
        toastService.success(ENTITY_LABELS.RECORD_SAVED_OFFLINE);
        createdEntity = recordForDb;
      }

      if (submitAction === 'save_and_continue') {
        onEntityCreated(createdEntity);
      } else if (submitAction === 'save_and_new') {
        setFormData({});
        refreshLocation();
      } else if (submitAction === 'save') {
        navigate(-1);
      }
    } catch {
      toastService.error(isOnline ? 'Failed to create record' : 'Failed to save record offline');
    } finally {
      setCreating(false);
    }
  };

  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse">
            <div className="h-3 bg-gray-200 rounded w-1/3 mb-2" />
            <div className="h-12 bg-gray-100 rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────
  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* GPS modal — fires the moment GPS is denied/unavailable or location fetch failed after first attempt */}
      {!readOnly && (permissionDenied || positionUnavailable || (locationFetched && !hasLocation)) && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="p-6 text-center space-y-4">
              <div className="w-16 h-16 bg-orange-100 text-orange-500 rounded-full flex items-center justify-center mx-auto">
                <LocationIcon className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-black text-gray-900">Location Required</h3>
              <p className="text-sm text-gray-500 font-medium px-4">
                {permissionDenied
                  ? "Location permission is blocked. Please enable it in your browser or device settings."
                  : positionUnavailable
                  ? "Your device GPS is turned off. Please enable it to continue."
                  : "We couldn't get your location. Please enable GPS and try again."}
              </p>
              <div className="pt-2">
                <button
                  type="button"
                  onClick={refreshLocation}
                  disabled={isLocationLoading}
                  className="w-full py-3 px-4 bg-[var(--btn-primary-bg)] text-white font-bold rounded-xl hover:opacity-90 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <RefreshIcon spinning={isLocationLoading} />
                  {isLocationLoading ? 'Fetching…' : 'Try Again'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dynamic schema fields */}
      {recordType?.schema_json?.length > 0 && (
        <div className="bg-white rounded-xl border-2 border-gray-200 p-4 shadow-sm space-y-4">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{ENTITY_LABELS.RECORD_DETAILS}</p>
          {recordType.schema_json.map(field => {
            const fieldKey = field.key;
            const fieldValue = formData[fieldKey] || '';
            const fieldType = field.type?.toLowerCase();

            // 1. Handle Select / Dropdown
            if (['select', 'select_one', 'dropdown'].includes(fieldType)) {
              return (
                <div key={fieldKey} className="space-y-2.5">
                  <Label className="text-sm font-semibold text-gray-700">{field.label}</Label>
                  <CustomDropdown
                    options={field.options || []}
                    value={fieldValue}
                    onChange={val => setFormData(prev => ({ ...prev, [fieldKey]: val }))}
                    disabled={readOnly}
                    placeholder={field.description || `Select ${field.label}…`}
                  />
                </div>
              );
            }

            // 2. Handle Date
            if (fieldType === 'date') {
              return (
                <div key={fieldKey} className="space-y-2.5">
                  <Label className="text-sm font-semibold text-gray-700">{field.label}</Label>
                  <SingleDatePicker
                    value={fieldValue}
                    onChange={val => setFormData(prev => ({ ...prev, [fieldKey]: val }))}
                    placeholder={field.description || "dd-mm-yyyy"}
                    isRequired={field.required}
                    disabled={readOnly}
                  />
                </div>
              );
            }

            // 3. Handle Textarea
            if (fieldType === 'textarea') {
              return (
                <div key={fieldKey} className="space-y-2.5">
                  <Label className="text-sm font-semibold text-gray-700">{field.label}</Label>
                  <textarea
                    placeholder={field.description || `Enter ${field.label}…`}
                    value={fieldValue}
                    onChange={e => setFormData(prev => ({ ...prev, [fieldKey]: e.target.value }))}
                    disabled={readOnly}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:border-[var(--btn-primary-bg)] focus:bg-[var(--btn-primary-bg)]/3 transition-all outline-none resize-none"
                  />
                </div>
              );
            }

            // 4. Default: Input (text, number, tel, etc.)
            return (
              <Input
                key={fieldKey}
                label={field.label}
                placeholder={field.description || `Enter ${field.label}…`}
                required={field.required}
                disabled={readOnly}
                type={fieldType === 'tel' ? 'tel' : fieldType === 'number' ? 'number' : 'text'}
                value={fieldValue}
                onChange={e => setFormData(prev => ({ ...prev, [fieldKey]: e.target.value }))}
              />
            );
          })}
        </div>
      )}

      {/* Status dropdown */}
      {recordType?.statuses_json?.length > 0 && (
        <div className="bg-white rounded-xl border-2 border-gray-200 p-4 shadow-sm space-y-3">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</p>
          <CustomDropdown
            options={recordType.statuses_json.map(s => ({ value: s.value, label: s.label }))}
            value={status}
            onChange={setStatus}
            disabled={readOnly}
            placeholder="Select status…"
          />
        </div>
      )}

      {/* Action buttons */}
      {effectiveMode === 'create' && !initialData ? (
        <div className="flex flex-col gap-3">
          <Button
            type="submit"
            fullWidth
            loading={creating && submitAction === 'save_and_continue'}
            variant="primary"
            size="large"
            className="font-semibold"
            onClick={() => setSubmitAction('save_and_continue')}
          >
            Save & Continue
          </Button>
          <div className="flex gap-3">
            <Button
              type="submit"
              fullWidth
              loading={creating && submitAction === 'save'}
              variant="secondary"
              size="large"
              className="font-semibold flex-1"
              onClick={() => setSubmitAction('save')}
            >
              Save
            </Button>
            <Button
              type="submit"
              fullWidth
              loading={creating && submitAction === 'save_and_new'}
              variant="secondary"
              size="large"
              className="font-semibold flex-1"
              onClick={() => setSubmitAction('save_and_new')}
            >
              Save & New
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="submit"
          fullWidth
          loading={creating}
          variant="primary"
          size="large"
          className="font-semibold"
          icon={<CheckIcon />}
        >
          {effectiveMode === 'update' ? 'Update & Continue' : 'Confirm & Continue'}
        </Button>
      )}
    </form>
  );
};

export default EntityCreationMiniForm;
